import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import styles from './LangSwitcher.module.scss'

const LANGS = [
  { code: 'ru', label: 'RU', name: 'Русский', prompt: 'Выбрать язык' },
  { code: 'ky', label: 'KY', name: 'Кыргызча', prompt: 'Тилди тандоо' },
  { code: 'en', label: 'EN', name: 'English', prompt: 'Choose language' },
]

const LangSwitcher = () => {
  const { i18n } = useTranslation()
  const language = (i18n.resolvedLanguage || i18n.language || 'ru').split('-')[0].toLowerCase()
  const current = LANGS.find((item) => item.code === language) || LANGS[0]
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const triggerRef = useRef(null)
  const optionRefs = useRef([])
  const menuId = useId()
  const currentIndex = LANGS.indexOf(current)

  useEffect(() => { document.documentElement.lang = current.code }, [current.code])

  useEffect(() => {
    if (!open) return undefined
    optionRefs.current[currentIndex]?.focus()
    const outside = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('focusin', outside)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('focusin', outside)
    }
  }, [open, currentIndex])

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const choose = (code) => {
    if (code !== current.code) {
      try { localStorage.setItem('lang', code) } catch (_) { /* i18next still changes the language without storage */ }
      i18n.changeLanguage(code)
      document.documentElement.lang = code
    }
    close()
  }

  const onMenuKey = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      close()
    } else if (event.key === 'Tab') {
      close()
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault()
      const focused = optionRefs.current.indexOf(document.activeElement)
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? LANGS.length - 1
        : (focused + (event.key === 'ArrowDown' ? 1 : -1) + LANGS.length) % LANGS.length
      optionRefs.current[next]?.focus()
    }
  }

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button ref={triggerRef} type="button" className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`} aria-label={`${current.prompt}: ${current.name}`} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen((value) => !value)} onKeyDown={(event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true) }
      }}>
        <Icon name="globe" size={17} />
        <span className={styles.languageName} lang={current.code}>{current.name}</span>
        <span className={styles.languageCode} aria-hidden="true">{current.label}</span>
        <Icon name="chevron-down" size={14} className={styles.chevron} />
      </button>
      {open && <div id={menuId} className={styles.dropdown} role="menu" aria-label={current.prompt} onKeyDown={onMenuKey}>
        {LANGS.map((item, index) => <button ref={(element) => { optionRefs.current[index] = element }} type="button" key={item.code} className={styles.option} role="menuitemradio" aria-checked={item.code === current.code} lang={item.code} tabIndex={-1} onClick={() => choose(item.code)}>
          <span className={styles.optionCode} aria-hidden="true">{item.label}</span>
          <span>{item.name}</span>
          {item.code === current.code && <Icon name="check" size={17} className={styles.check} />}
        </button>)}
      </div>}
    </div>
  )
}

export default LangSwitcher
